import {FC, useContext, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Badge, Button, Form, Modal} from 'react-bootstrap'
import FdsModalContext from '../../../../../_fds/layout/ModalContext'
import {useToast} from '../../../../../_fds/layout/components/toast-messages'
import {useStore} from '../../store'
import {LaborTemplateListItemModel} from '../../core/_models'
import {
  deleteLaborTemplate,
  getLaborTemplate,
  getLaborTemplates,
  updateLaborTemplate,
} from '../../core/_requests'
import {useTemplateMode} from './useTemplateMode'
import {FdsSelect} from '../common/FdsSelect'

const modalsRoot = document.getElementById('root-modals') || document.body

type TemplateManagerModalProps = {
  show: boolean
  onHide: () => void
}

// Entry point of template mode: list / open / rename / delete the location's
// saved templates, or create a new (empty, 10-row) daily/weekly draft.
const TemplateManagerModal: FC<TemplateManagerModalProps> = ({show, onHide}) => {
  const {selectedLocationId} = useStore()
  const {enterTemplateMode, openTemplate} = useTemplateMode()
  const {setSettings} = useContext(FdsModalContext)
  const {showToast} = useToast()

  const [templates, setTemplates] = useState<LaborTemplateListItemModel[]>([])
  const [loading, setLoading] = useState(false)
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<'daily' | 'weekly'>('daily')
  const [renameUuid, setRenameUuid] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const refresh = async () => {
    setLoading(true)
    const list = await getLaborTemplates(selectedLocationId)
    setTemplates(list || [])
    setLoading(false)
  }

  useEffect(() => {
    if (show) {
      setNewName('')
      setRenameUuid(null)
      refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedLocationId])

  const nameTaken = (name: string, excludeUuid?: string | null) =>
    templates.some(
      (t) => t.uuid !== excludeUuid && t.name.trim().toLowerCase() === name.trim().toLowerCase()
    )

  const handleCreate = () => {
    const name = newName.trim()
    if (!name) return
    if (nameTaken(name)) {
      showToast('Duplicate Name', 'A template with this name already exists.', 'danger')
      return
    }
    onHide()
    enterTemplateMode(null, {name, template_type: newType})
  }

  const handleOpen = async (uuid: string) => {
    onHide()
    await openTemplate(uuid)
  }

  const handleRename = async (template: LaborTemplateListItemModel) => {
    const name = renameValue.trim()
    if (!name || name === template.name) {
      setRenameUuid(null)
      return
    }
    if (nameTaken(name, template.uuid)) {
      showToast('Duplicate Name', 'A template with this name already exists.', 'danger')
      return
    }
    try {
      // PUT is a full replace (name + shifts) — fetch the shifts first so a
      // rename never wipes the template's contents.
      const full = await getLaborTemplate(template.uuid)
      if (!full) throw new Error('template not found')
      await updateLaborTemplate(template.uuid, {name, shifts: full.shifts})
      showToast('Template Renamed', `Template renamed to '${name}'.`, 'success')
      setRenameUuid(null)
      refresh()
    } catch (error) {
      showToast('Error', 'Unable to rename template!', 'danger')
    }
  }

  const handleDelete = (template: LaborTemplateListItemModel) => {
    setSettings({
      cancelButtonText: 'Close',
      saveButtonText: 'Delete template',
      saveButtonCallback: async () => {
        try {
          await deleteLaborTemplate(template.uuid)
          showToast('Template Deleted', `Template '${template.name}' deleted.`, 'success')
          refresh()
        } catch (error) {
          showToast('Error', 'Unable to delete template!', 'danger')
        }
      },
      closeButtonCallback: () => {},
      titleText: 'Delete Template',
      content: (
        <div>
          <div className='text-gray-600 fs-6 fw-bold'>
            Are you sure you want to delete template '{template.name}'?
          </div>
          <div className='text-danger fs-7 fw-bold'>This action cannot be undone.</div>
        </div>
      ),
      show: true,
    })
  }

  return createPortal(
    <Modal
      id='fds_modal_template_manager'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-650px'
      show={show}
      onHide={onHide}
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>Schedule Templates</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-8'>
          <label className='form-label fw-bold'>Create a new template:</label>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            <Form.Control
              size='sm'
              type='text'
              className='w-200px'
              placeholder='Template name'
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <FdsSelect<'daily' | 'weekly'>
              ariaLabel='Template type'
              value={newType}
              onChange={setNewType}
              options={[
                {value: 'daily', label: 'Daily'},
                {value: 'weekly', label: 'Weekly'},
              ]}
            />
            <Button size='sm' variant='primary' disabled={!newName.trim()} onClick={handleCreate}>
              Create
            </Button>
          </div>
        </div>

        <label className='form-label fw-bold'>Saved templates:</label>
        {loading ? (
          <div className='text-gray-600 fs-7'>Loading…</div>
        ) : templates.length === 0 ? (
          <div className='text-gray-600 fs-7'>No templates saved for this store yet.</div>
        ) : (
          <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3'>
            <thead>
              <tr className='fs-7 text-gray-500'>
                <th>Name</th>
                <th>Type</th>
                <th>Shifts</th>
                <th className='text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.uuid}>
                  <td>
                    {renameUuid === template.uuid ? (
                      <Form.Control
                        size='sm'
                        type='text'
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(template)
                          if (e.key === 'Escape') setRenameUuid(null)
                        }}
                      />
                    ) : (
                      <span className='fw-bold'>{template.name}</span>
                    )}
                  </td>
                  <td>
                    <Badge bg={template.template_type === 'weekly' ? 'info' : 'secondary'}>
                      {template.template_type === 'weekly' ? 'Weekly' : 'Daily'}
                    </Badge>
                  </td>
                  <td>{template.shift_count}</td>
                  <td className='text-end'>
                    {renameUuid === template.uuid ? (
                      <>
                        <Button
                          size='sm'
                          variant='primary'
                          className='me-1'
                          onClick={() => handleRename(template)}
                        >
                          Save
                        </Button>
                        <Button size='sm' variant='light' onClick={() => setRenameUuid(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size='sm'
                          variant='primary'
                          className='me-1'
                          onClick={() => handleOpen(template.uuid)}
                        >
                          Open
                        </Button>
                        <Button
                          size='sm'
                          variant='light'
                          className='me-1'
                          onClick={() => {
                            setRenameUuid(template.uuid)
                            setRenameValue(template.name)
                          }}
                        >
                          Rename
                        </Button>
                        <Button
                          size='sm'
                          variant='light-danger'
                          onClick={() => handleDelete(template)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal.Body>
    </Modal>,
    modalsRoot
  )
}

export {TemplateManagerModal}
